import React, { useState, useEffect } from 'react';
import { usePWA } from '../../hooks/usePWA';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Download, Bell, BellOff, Wifi, WifiOff, X, 
  Smartphone, CheckCircle, Settings
} from 'lucide-react';

/**
 * PWA Install Banner - Shows install prompt and notification settings
 */
export const PWAInstallBanner = ({ appType = 'driver', onDismiss }) => {
  const {
    isInstallable,
    isInstalled,
    isOnline,
    pushSupported,
    pushPermission,
    installApp,
    requestPushPermission
  } = usePWA(appType);

  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [triggeredFromDropdown, setTriggeredFromDropdown] = useState(false);

  // Detect iOS and Android
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  // App names and colors
  const appConfig = {
    dispatch: { name: 'Drayly Dispatch', color: 'blue', icon: '📋' },
    driver: { name: 'Drayly Driver', color: 'emerald', icon: '🚛' },
    'owner-operator': { name: 'Owner Operator', color: 'green', icon: '📊' },
    timeclock: { name: 'TimeClock Pro', color: 'amber', icon: '⏱️' }
  };

  const config = appConfig[appType] || appConfig.driver;

  useEffect(() => {
    // Check if already dismissed
    const wasDismissed = localStorage.getItem(`pwa-banner-dismissed-${appType}`);
    if (wasDismissed) {
      setDismissed(true);
    }
    
    // Auto-suppress on desktop-sized viewports (>=1024px) — banner is for mobile.
    // Desktop users can still trigger it from the profile dropdown
    // (`trigger-pwa-install` event), which sets triggeredFromDropdown=true.
    const isDesktopViewport = typeof window !== 'undefined' && window.innerWidth >= 1024;

    // Show banner if installable or notifications not enabled
    if (
      !isDesktopViewport &&
      (isInstallable || (pushSupported && pushPermission !== 'granted')) &&
      !wasDismissed
    ) {
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, pushSupported, pushPermission, appType]);

  // Listen for "Install App" trigger from profile dropdown
  useEffect(() => {
    const handleTrigger = () => {
      if (isInstallable) {
        // Browser supports native install prompt (Android Chrome mostly)
        installApp();
      } else if (isInstalled) {
        const { toast } = require('sonner');
        toast.info('App is already installed!');
      } else {
        // Show banner with platform-specific instructions (iOS, Android, Desktop)
        setTriggeredFromDropdown(true);
        setDismissed(false);
        setShowBanner(true);
        localStorage.removeItem(`pwa-banner-dismissed-${appType}`);
      }
    };
    window.addEventListener('trigger-pwa-install', handleTrigger);
    return () => window.removeEventListener('trigger-pwa-install', handleTrigger);
  }, [isInstallable, isInstalled, installApp, appType]);

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem(`pwa-banner-dismissed-${appType}`, 'true');
    onDismiss?.();
  };

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      setShowBanner(false);
    }
  };

  const handleEnableNotifications = async () => {
    await requestPushPermission();
  };

  if ((!showBanner && !triggeredFromDropdown) || dismissed || isInstalled) return null;
  if (!showBanner) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4">
      <Card className="bg-slate-900 border-slate-700 shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{config.icon}</span>
              <div>
                <h3 className="text-white font-semibold">{config.name}</h3>
                <p className="text-slate-400 text-sm">Install for better experience</p>
              </div>
            </div>
            <button
              data-testid="pwa-install-banner-close"
              onClick={handleDismiss}
              className="text-slate-500 hover:text-white p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-3">
            {/* Native Install Button (Android Chrome with beforeinstallprompt) */}
            {isInstallable && (
              <Button
                onClick={handleInstall}
                className={`w-full bg-${config.color}-600 hover:bg-${config.color}-700`}
              >
                <Download className="h-4 w-4 mr-2" />
                Install App
              </Button>
            )}

            {/* iOS Instructions */}
            {!isInstallable && isIOS && (
              <div className="bg-slate-800/80 rounded-lg p-3 space-y-2">
                <p className="text-white text-sm font-medium">To install on iPhone/iPad:</p>
                <div className="space-y-1.5">
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                    Tap the <span className="inline-flex items-center mx-0.5"><svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg> Share</span> button at the bottom
                  </p>
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                    Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong>
                  </p>
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span>
                    Tap <strong className="text-white">"Add"</strong> to confirm
                  </p>
                </div>
              </div>
            )}

            {/* Android Instructions (when beforeinstallprompt didn't fire) */}
            {!isInstallable && isAndroid && (
              <div className="bg-slate-800/80 rounded-lg p-3 space-y-2">
                <p className="text-white text-sm font-medium">To install on Android:</p>
                <div className="space-y-1.5">
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                    Tap the <strong className="text-white">&#8942;</strong> menu (3 dots) in Chrome
                  </p>
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                    Tap <strong className="text-white">"Add to Home screen"</strong> or <strong className="text-white">"Install app"</strong>
                  </p>
                  <p className="text-slate-300 text-xs flex items-center gap-2">
                    <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span>
                    Tap <strong className="text-white">"Install"</strong> to confirm
                  </p>
                </div>
              </div>
            )}

            {/* Desktop fallback (when beforeinstallprompt didn't fire) */}
            {!isInstallable && !isMobile && (
              <div className="bg-slate-800/80 rounded-lg p-3 space-y-2">
                <p className="text-white text-sm font-medium">To install on desktop:</p>
                <p className="text-slate-300 text-xs">
                  Click the install icon <Download className="h-3 w-3 inline mx-0.5" /> in your browser's address bar, or use the browser menu &gt; "Install app"
                </p>
              </div>
            )}

            {/* Enable Notifications */}
            {pushSupported && pushPermission !== 'granted' && (
              <Button
                onClick={handleEnableNotifications}
                variant="outline"
                className="w-full border-slate-700"
              >
                <Bell className="h-4 w-4 mr-2" />
                Enable Notifications
              </Button>
            )}

            {/* Benefits */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                <Smartphone className="h-3 w-3 mr-1" />
                Works offline
              </Badge>
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                <Bell className="h-3 w-3 mr-1" />
                Push alerts
              </Badge>
              <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                <CheckCircle className="h-3 w-3 mr-1" />
                Faster
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * PWA Status Indicator - Shows connection and notification status
 */
export const PWAStatusIndicator = ({ appType = 'driver' }) => {
  const { isOnline, pushPermission, isInstalled } = usePWA(appType);

  return (
    <div className="flex items-center gap-2">
      {/* Online Status */}
      <div className={`flex items-center gap-1 text-xs ${isOnline ? 'text-green-400' : 'text-red-400'}`}>
        {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
        <span className="hidden sm:inline">{isOnline ? 'Online' : 'Offline'}</span>
      </div>

      {/* Notification Status */}
      <div className={`flex items-center gap-1 text-xs ${pushPermission === 'granted' ? 'text-green-400' : 'text-slate-500'}`}>
        {pushPermission === 'granted' ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
      </div>

      {/* Installed Badge */}
      {isInstalled && (
        <Badge className="bg-purple-500/20 text-purple-400 text-xs">
          PWA
        </Badge>
      )}
    </div>
  );
};

/**
 * PWA Settings Panel - Full settings for PWA features
 */
export const PWASettingsPanel = ({ appType = 'driver' }) => {
  const {
    isInstallable,
    isInstalled,
    isOnline,
    pushSupported,
    pushPermission,
    pushSubscription,
    installApp,
    requestPushPermission,
    unsubscribeFromPush,
    checkForUpdates
  } = usePWA(appType);

  const [checking, setChecking] = useState(false);

  const handleCheckUpdates = async () => {
    setChecking(true);
    await checkForUpdates();
    setTimeout(() => setChecking(false), 1000);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Settings className="h-5 w-5" />
        App Settings
      </h3>

      {/* Installation Status */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">App Installation</h4>
              <p className="text-slate-400 text-sm">
                {isInstalled ? 'App is installed' : 'Install for offline access'}
              </p>
            </div>
            {isInstalled ? (
              <Badge className="bg-green-500/20 text-green-400">
                <CheckCircle className="h-3 w-3 mr-1" />
                Installed
              </Badge>
            ) : isInstallable ? (
              <Button size="sm" onClick={installApp}>
                <Download className="h-4 w-4 mr-2" />
                Install
              </Button>
            ) : (
              <Badge className="bg-slate-700 text-slate-400">
                Not available
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Push Notifications</h4>
              <p className="text-slate-400 text-sm">
                {pushPermission === 'granted' 
                  ? 'Notifications enabled' 
                  : pushPermission === 'denied'
                  ? 'Notifications blocked'
                  : 'Get alerts for new dispatches'}
              </p>
            </div>
            {pushSupported ? (
              pushPermission === 'granted' ? (
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/20 text-green-400">
                    <Bell className="h-3 w-3 mr-1" />
                    Enabled
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={unsubscribeFromPush}>
                    <BellOff className="h-4 w-4" />
                  </Button>
                </div>
              ) : pushPermission === 'denied' ? (
                <Badge className="bg-red-500/20 text-red-400">
                  Blocked
                </Badge>
              ) : (
                <Button size="sm" onClick={requestPushPermission}>
                  <Bell className="h-4 w-4 mr-2" />
                  Enable
                </Button>
              )
            ) : (
              <Badge className="bg-slate-700 text-slate-400">
                Not supported
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Connection Status */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Connection Status</h4>
              <p className="text-slate-400 text-sm">
                {isOnline ? 'Connected to server' : 'Working offline'}
              </p>
            </div>
            <Badge className={isOnline ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
              {isOnline ? <Wifi className="h-3 w-3 mr-1" /> : <WifiOff className="h-3 w-3 mr-1" />}
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Check for Updates */}
      <Button 
        variant="outline" 
        className="w-full border-slate-700"
        onClick={handleCheckUpdates}
        disabled={checking}
      >
        {checking ? 'Checking...' : 'Check for Updates'}
      </Button>
    </div>
  );
};

export default PWAInstallBanner;
